import {Box, Button} from '@mui/material';
import {useCan, usePersona} from 'src/permissions/hooks';
import {FC, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';
import {employeeApi, usersApi} from 'src/api';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {store} from 'src/app/store';
import {PowerConsumption} from 'src/components/common/PowerConsumption';
import {UserDetails} from 'src/components/users/UserDetails';
import {UserIdentityCard} from 'src/components/users/UserIdentityCard';
import {UserLinkedRail} from 'src/components/users/UserLinkedRail';
import {CompanyLinkedRail} from 'src/components/users/CompanyLinkedRail';
import {buildLinkedRows, buildUserFacts, isBackOfficeUser} from 'src/components/users/userProfile';
import {actionButtonSx} from 'src/components/common/actionButton.sx';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {UserByIdRequest} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import AMPModal from 'src/components/common/AMPModal';
import {EmployeeForm} from 'src/components/forms/EmployeeForm';
import {UpdateEmployeeRequest} from 'src/types/employee';
import {ContractsHistory} from '../../components/common/ContractsHistory';
import {pageSx, twoColumnSx} from './UserInfo.sx';
import {UserTypeEnum} from 'src/enums';
import {CompanyDetails} from 'src/components/users/CompanyDetails';
import {CompanyInformation} from '../../components/common/CompanyInformation/CompanyInformation';
import {EmployeeInformation} from '../../components/common/EmployeeInformation';
import {DriverInformation} from '../../components/common/DriverInformation';
import {EditIcon} from 'src/components/icons/EditIcon';
import {UserResetWrapper} from 'src/components/users/UserResetWrapper';

type UserQueryTrigger = ReturnType<typeof usersApi.useLazyGetUserByIdQuery>[0];

const handleGetUserInfo =
    (trigger: UserQueryTrigger) =>
        async (data: UserByIdRequest) => {
            store.dispatch(preloaderChangeStatus(true));
            try {
                const response = await trigger(data).unwrap();
                store.dispatch(preloaderChangeStatus(false));
                return response;
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };

export const UserInfo: FC = () => {
    const params = useParams();
    const persona = usePersona();
    const {activeBusiness, authInfo} = useAppSelector((state) => state.common);
    const dispatch = useAppDispatch();
    const intl = useIntl();

    const [trigger, response] = usersApi.useLazyGetUserByIdQuery();
    const [showEditModal, setShowEditModal] = useState(false);
    const canUpdateEmployee = useCan('employees.update');
    const [triggerUpdateEmployee] = employeeApi.useUpdateEmployeeByIdMutation();


    const isCompanyUser = response?.data
        ? response?.data.userType === UserTypeEnum.COMPANY
        : false;

    const isEmployeeUser = response?.data
        ? response?.data.userType === UserTypeEnum.EMPLOYEE
        : false;

    const isDriverUser = response?.data
        ? response?.data.userType === UserTypeEnum.PRIVATE_DRIVER
        : false;
    // `isEmployeeUser` and `isCompanyUser` above describe the person being looked at - an employee has
    // no contract of their own, so there is nothing to list. This one described the person doing the
    // looking, and hid the history from every back-office role that holds contracts.read.
    const canReadContracts = useCan('contracts.read');
    const canHoldContracts = !isEmployeeUser && !isBackOfficeUser(response?.data?.userType);
    const canReadReports = useCan('reports.read');
    // The same three permissions the tabs below check before listing any of these — search, not
    // read: these are the endpoints that return the lists the counts come from.
    const canReadChargers = useCan('chargers.search');
    const canReadRfid = useCan('rfidTags.search');
    const canReadEmployees = useCan('employees.search');


    useEffect(() => {
        if (!Object.keys(authInfo)?.length || !params?.id) {
            return;
        }

        const data: UserByIdRequest = {userId: +params.id, businessId: activeBusiness};

        // No realEstateId or companyId from the account: the server forces whichever of them
        // matches the caller's reach onto the request, and for a caller whose reach is wider
        // they were never theirs to choose.

        handleGetUserInfo(trigger)(data);
        // On the id, not on mount. Going from one person to another keeps this component mounted -
        // it is the same route - so with an empty list the page kept showing whoever was open when
        // it first rendered. Clicking your own name from another user's page did nothing at all.
    }, [params?.id, authInfo, activeBusiness]);

    const handleSubmit = async (values: Omit<UpdateEmployeeRequest, 'businessId' | 'companyId'>) => {
        if (!response?.data?.employee) return;

        const data: UpdateEmployeeRequest = {
            ...values,
            businessId: response?.data?.business?.id ?? 0,
            companyId: response?.data?.employee?.company?.id ?? 0,
        };

        try {
            dispatch(preloaderChangeStatus(true));

            await triggerUpdateEmployee({employeeId: response?.data?.employee?.id ?? 0, data})
                .unwrap();

            NotifySuccess(intl.formatMessage({id: 'employeeUpdatedSuccessfully'}));
            setShowEditModal(false);
            // Refresh user data
            handleGetUserInfo(trigger)({userId: +params.id!, businessId: activeBusiness});
        } catch (error: any) {
            error.status === 400 && NotifyError(intl.formatMessage({id: 'cannotUpdateEmployee'}));
        } finally {
            dispatch(preloaderChangeStatus(false));
        }
    };

    const linkedRows = response?.data ? buildLinkedRows(response.data) : [];
    // A company's rail is counts rather than named links, so it is its own component with its own
    // queries; every other type builds its rows from the user payload that is already here.
    const showCompanyRail = isCompanyUser && Boolean(response?.data?.company?.id);
    const hasRail = showCompanyRail || linkedRows.length > 0;

    return (
        <Box sx={pageSx}>
            {response?.data && (
                // The person and what they are linked to, side by side and level. A type that can own
                // nothing - a manager, a property owner - gets no rail at all rather than an empty
                // one, and the card takes the whole row.
                <Box sx={hasRail ? twoColumnSx : undefined}>
                    {isCompanyUser ? (
                        <CompanyDetails data={response?.data}/>
                    ) : isEmployeeUser ? (
                        <UserIdentityCard
                            data={response.data}
                            title={`${response.data.firstName || ''} ${response.data.lastName || ''}`.trim()
                                || response.data.emailAddress || ''}
                            typeLabel={String(response.data.userType)}
                            facts={buildUserFacts(response.data)}
                            actions={(
                                <>
                                    {/* The same permission as before — an employee's details were
                                        editable from a pencil in the corner of a block written into
                                        this page. */}
                                    {canUpdateEmployee && (
                                        <Button
                                            variant="contained"
                                            sx={actionButtonSx('32px', 'secondary')}
                                            startIcon={<EditIcon/>}
                                            onClick={() => setShowEditModal(true)}
                                        >
                                            <FormattedMessage id="edit"/>
                                        </Button>
                                    )}
                                    <UserResetWrapper
                                        data={response.data}
                                        toolbar
                                    />
                                </>
                            )}
                        />
                    ) : (
                        <UserDetails data={response?.data}/>
                    )}

                    {showCompanyRail && (
                        <CompanyLinkedRail
                            businessId={response?.data?.business?.id ?? activeBusiness}
                            companyId={response?.data?.company?.id}
                            canReadChargers={canReadChargers}
                            canReadRfid={canReadRfid}
                            canReadEmployees={canReadEmployees}
                        />
                    )}
                    {!showCompanyRail && linkedRows.length > 0 && <UserLinkedRail rows={linkedRows}/>}
                </Box>
            )}

            {isCompanyUser ? (
                <CompanyInformation userData={response?.data ?? null}/>
            ) : isEmployeeUser ? (
                <EmployeeInformation
                    employeeData={response?.data?.employee ?? null}
                    businessId={response?.data?.business?.id ?? 0}
                    companyId={response?.data?.employee?.company?.id ?? 0}
                />
            ) : isDriverUser ? (
                <DriverInformation userData={response?.data ?? null}/>
            ) : null}

            {canReadReports && response?.data && (
                <PowerConsumption
                    companyId={isCompanyUser ? response?.data?.company?.id : undefined}
                    userId={!isCompanyUser ? response?.data?.id : undefined}
                />
            )}

            {/* Only the account types that can hold a contract. An employee charges under their
                company's, and nobody in the back office - a business owner, a manager, a property
                owner - signs one at all, so for them this was an empty table with a heading over it
                that would never fill. */}
            {canHoldContracts && canReadContracts && (
                <ContractsHistory contracts={response?.data?.contracts ?? []}/>
            )}

            {isEmployeeUser && response?.data?.employee && (
                <AMPModal
                    handleClose={() => setShowEditModal(false)}
                    open={showEditModal}
                    title={intl.formatMessage({id: 'editEmployee'})}
                    type="md"
                    fullWidth
                    withoutPaddings
                >
                    <EmployeeForm
                        handleClose={() => setShowEditModal(false)}
                        isEditMode
                        initialValues={{
                            firstName: response.data.employee.firstName,
                            lastName: response.data.employee.lastName,
                            title: response.data.employee.title,
                            consumptionPaymentMode: response.data.employee.consumptionPaymentMode,
                            language: response.data.language,
                        }}
                        onSubmit={handleSubmit}
                        onCancel={() => setShowEditModal(false)}
                        hidePaymentMode={persona.isEmployee}
                    />
                </AMPModal>
            )}
        </Box>
    );
};
